import { useEffect, useState } from 'react'
import { apiURL } from '../utilities/URLs'
import { customerData } from '../utilities/CustomerData'
import { refreshAccessToken, shouldShowPaymentInstallmentStatus, shouldShowUpdateCardOnFileTile } from '../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import CreditCardInput from 'react-credit-card-input'

/**
 * Replace card on file (EasyPay customer vault) only — no invoice payment or installment setup.
 */
export default function UpdateCardOnFile({ setReload }) {
  const navigate = useNavigate()

  useEffect(() => {
    if (!shouldShowUpdateCardOnFileTile() && !shouldShowPaymentInstallmentStatus())
      navigate('/dashboard')
  }, [navigate])

  const [cardInfo, setCardInfo] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })
  const [errors, setErrors] = useState({})
  const [updatingCardInfo, setUpdatingCardInfo] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [hasUnpaidInstallments, setHasUnpaidInstallments] = useState(false)

  const leadId = customerData.customerLeadData.leadId
  const isInstallmentCustomer = shouldShowPaymentInstallmentStatus()

  const fetchInstallmentPayInfo = async () => {
    if (!isInstallmentCustomer)
      return

    try {
      let res = await fetch(
        `${apiURL}/api/paymentInstallmentStatus?leadId=${encodeURIComponent(leadId)}`,
        { headers: { Authorization: customerData.accessToken } }
      )
      let data = await res.json()

      if (data.response?.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken && refreshedToken.length > 0) {
          res = await fetch(
            `${apiURL}/api/paymentInstallmentStatus?leadId=${encodeURIComponent(leadId)}`,
            { headers: { Authorization: customerData.accessToken } }
          )
          data = await res.json()
        } else {
          navigate('/', { state: { expired: true } })
          return
        }
      }

      if (!data.response?.error) {
        const unpaidCount = data.response.unpaidInstallments?.length ?? 0
        setHasUnpaidInstallments(unpaidCount > 0 && !!data.response.canPayNextInstallment)
      }
    } catch {
      // Non-blocking — card update already succeeded
    }
  }

  const handleCardNumberChange = (e) => {
    setCardInfo({ ...cardInfo, cardNumber: e.target.value })
  }

  const handleCardExpiryChange = (e) => {
    setCardInfo({ ...cardInfo, expiryDate: e.target.value })
  }

  const handleCardCVCChange = (e) => {
    setCardInfo({ ...cardInfo, cvv: e.target.value })
  }

  const handleUpdateCardInfo = async () => {
    const newErrors = {}

    if (!cardInfo.cardNumber) newErrors.cardNumber = 'Card Number is required'
    if (!cardInfo.expiryDate) newErrors.expiryDate = 'Expiry Date is required'
    if (!cardInfo.cvv) newErrors.cvv = 'CVV is required'

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0)
      return

    setUpdatingCardInfo(true)
    setError('')
    const form = new FormData()
    form.append('cardInfo', JSON.stringify(cardInfo))

    const updateCardResponse = await fetch(`${apiURL}/api/updateCardInfo?leadId=${encodeURIComponent(leadId)}`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const updateCardData = await updateCardResponse.json()
    if (updateCardData.response.error) {
      if (updateCardData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleUpdateCardInfo() : navigate('/', { state: { expired: true } })
      } else {
        console.error(updateCardData.response.error)
        setError(updateCardData.response.error)
      }
    } else {
      setSaved(true)
      await fetchInstallmentPayInfo()
    }

    setUpdatingCardInfo(false)
  }

  return (
    <div className='flex flex-col items-center w-full px-4 py-8'>
      <p className='text-3xl font-bold text-center mb-2'>Update card on file</p>
      <p className='text-center text-gray-600 max-w-lg mb-6'>
        Enter your card details to replace the card we keep on file for your account. This updates your payment profile only — no payment is processed here unless you choose to pay your installment below.
      </p>

      {!saved ? (
        <div className='bg-white shadow-lg rounded-xl p-6 w-full max-w-lg'>
          <div className='space-y-5'>
            <div>
              <label htmlFor='cardNumber' className='block text-sm font-medium text-gray-700 mb-1'>
                Card Information
              </label>
              <CreditCardInput
                cardNumberInputProps={{
                  value: cardInfo.cardNumber,
                  onChange: handleCardNumberChange,
                }}
                cardExpiryInputProps={{
                  value: cardInfo.expiryDate,
                  onChange: handleCardExpiryChange,
                }}
                cardCVCInputProps={{
                  value: cardInfo.cvv,
                  onChange: handleCardCVCChange,
                }}
                fieldClassName='p-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition'
                containerClassName='w-full space-y-2'
              />
            </div>
            <div className='text-xs text-red-500 space-y-1'>
              {errors.cardNumber && <p>{errors.cardNumber}</p>}
              {errors.expiryDate && <p>{errors.expiryDate}</p>}
              {errors.cvv && <p>{errors.cvv}</p>}
            </div>
            {error && (
              <p className='text-sm text-red-500'>{error}</p>
            )}
            <button
              type='submit'
              onClick={handleUpdateCardInfo}
              className='w-full text-white p-2 rounded-md font-semibold bg-primaryColor hover:bg-primaryDarkColor transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed'
              disabled={updatingCardInfo || !cardInfo.cardNumber || !cardInfo.cvv || !cardInfo.expiryDate}
            >
              {updatingCardInfo ? 'Saving card...' : 'Save card on file'}
            </button>
          </div>
        </div>
      ) : (
        <div className='bg-white shadow-lg rounded-xl p-6 w-full max-w-lg text-center'>
          <p className='text-lg font-medium text-gray-800 mb-4'>Your card on file has been updated.</p>

          {isInstallmentCustomer && hasUnpaidInstallments && (
            <div className='mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-left'>
              <p className='font-semibold text-[#646464] mb-2'>Make Payment Now</p>
              <p className='text-sm text-gray-600 mb-4'>
                Your card on file has been updated. Go to your dashboard to choose which installments to pay in one batch.
              </p>
              <button
                type='button'
                onClick={() => navigate('/dashboard')}
                className='w-full text-white p-3 rounded-lg font-semibold bg-green-600 hover:bg-green-700 transition duration-200'
              >
                Pay Now
              </button>
            </div>
          )}

          <p className='text-sm text-gray-600 mb-6'>You can return to your dashboard anytime.</p>
          <button
            onClick={() => { setReload(true); navigate('/dashboard') }}
            className='w-full text-white p-2 rounded-md font-semibold bg-primaryColor hover:bg-primaryDarkColor transition duration-200'
          >
            Return to dashboard
          </button>
        </div>
      )}
    </div>
  )
}
